import { watch, type App, type Plugin } from 'vue';
import type { Router, RouteLocationNormalizedLoaded } from 'vue-router';

import { BUGIAM_USER_CLEAR_EVENT, BUGIAM_USER_SET_EVENT } from '@/integrations/bugiam-events';
import { authState } from '@/stores/auth';

type BugIAmLevel = 'info' | 'warning' | 'error' | 'critical';

interface BugIAmBreadcrumb {
  timestamp?: string;
  category?: string;
  message: string;
  data?: unknown;
}

interface BugIAmOptions {
  projectId: string;
  apiKey: string;
  baseUrl: string;
  release: string;
  router?: Router;
  maxBreadcrumbs?: number;
  defaultLevel?: BugIAmLevel;
}

interface BugIAmClient {
  init(): Promise<void>;
  setUser(user: unknown): void;
  addBreadcrumb(breadcrumb: BugIAmBreadcrumb): void;
  captureError(error: unknown, options?: { level?: BugIAmLevel; errorCode?: string; referrer?: string }): Promise<void>;
}

const BUGIAM_MAX_BREADCRUMBS = 50;
const SESSION_CACHE_PREFIX = 'bugiam:session:';
const VISITOR_CACHE_PREFIX = 'bugiam:visitor:';

interface CachedBugIAmSession {
  sessionId: string;
  expiresAt: string;
  userKey: string;
}

declare module 'vue' {
  interface ComponentCustomProperties {
    $bugIam: BugIAmClient;
  }
}

function normalizeBaseUrl(baseUrl: string): string {
  return baseUrl.replace(/\/$/, '');
}

function serializeError(error: unknown) {
  const fallbackMessage =
    typeof error === 'string'
      ? error
      : error && typeof error === 'object' && 'message' in error && typeof error.message === 'string'
        ? error.message
        : 'Unbekannter Fehler';
  const name =
    error && typeof error === 'object' && 'name' in error && typeof error.name === 'string'
      ? error.name
      : 'Error';
  const stackTrace =
    error && typeof error === 'object' && 'stack' in error && typeof error.stack === 'string'
      ? error.stack
      : `${name}: ${fallbackMessage}`;

  return {
    name,
    message: fallbackMessage,
    stackTrace,
  };
}

function stableStringify(value: unknown): string {
  if (value === null || value === undefined) {
    return 'null';
  }

  if (typeof value !== 'object') {
    return JSON.stringify(value);
  }

  if (Array.isArray(value)) {
    return `[${value.map((entry) => stableStringify(entry)).join(',')}]`;
  }

  const entries = Object.entries(value as Record<string, unknown>).sort(([left], [right]) => left.localeCompare(right));
  return `{${entries.map(([key, entry]) => `${JSON.stringify(key)}:${stableStringify(entry)}`).join(',')}}`;
}

function sessionCacheKey(baseUrl: string, projectId: string): string {
  return `${SESSION_CACHE_PREFIX}${baseUrl}:${projectId}`;
}

function getSessionStorage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function getLocalStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function visitorCacheKey(baseUrl: string, projectId: string): string {
  return `${VISITOR_CACHE_PREFIX}${baseUrl}:${projectId}`;
}

function createAnonymousVisitorId(): string {
  const randomSource = typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;
  return `anon_${String(randomSource).replace(/[^a-zA-Z0-9_-]/g, '')}`;
}

function createClient(options: BugIAmOptions): BugIAmClient {
  const baseUrl = normalizeBaseUrl(options.baseUrl);
  const maxBreadcrumbs = Math.min(options.maxBreadcrumbs ?? BUGIAM_MAX_BREADCRUMBS, BUGIAM_MAX_BREADCRUMBS);
  const defaultLevel = options.defaultLevel ?? 'error';
  const cacheKey = sessionCacheKey(baseUrl, options.projectId);
  const anonymousVisitorKey = visitorCacheKey(baseUrl, options.projectId);
  let user: unknown = null;
  let sessionId: string | null = null;
  let breadcrumbs: BugIAmBreadcrumb[] = [];
  let autoTrackingBound = false;

  const currentUserKey = () => stableStringify(user);

  const readCachedSession = (): CachedBugIAmSession | null => {
    const storage = getSessionStorage();

    if (!storage) {
      return null;
    }

    try {
      const raw = storage.getItem(cacheKey);
      if (!raw) {
        return null;
      }

      const cached = JSON.parse(raw) as CachedBugIAmSession;
      if (
        !cached?.sessionId ||
        !cached?.expiresAt ||
        cached.userKey !== currentUserKey() ||
        new Date(cached.expiresAt).getTime() <= Date.now()
      ) {
        storage.removeItem(cacheKey);
        return null;
      }

      return cached;
    } catch {
      storage.removeItem(cacheKey);
      return null;
    }
  };

  const writeCachedSession = (nextSessionId: string, expiresAt: string) => {
    const storage = getSessionStorage();

    if (!storage) {
      return;
    }

    storage.setItem(
      cacheKey,
      JSON.stringify({
        sessionId: nextSessionId,
        expiresAt,
        userKey: currentUserKey(),
      } satisfies CachedBugIAmSession),
    );
  };

  const clearCachedSession = () => {
    sessionId = null;
    getSessionStorage()?.removeItem(cacheKey);
  };

  const getAnonymousVisitorId = (): string => {
    const storage = getLocalStorage();

    if (!storage) {
      return createAnonymousVisitorId();
    }

    const existing = storage.getItem(anonymousVisitorKey);
    if (existing?.trim()) {
      return existing.trim();
    }

    const created = createAnonymousVisitorId();
    storage.setItem(anonymousVisitorKey, created);
    return created;
  };

  const bindAutoTracking = () => {
    if (autoTrackingBound) {
      return;
    }

    autoTrackingBound = true;
    window.addEventListener('error', handleWindowError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
  };

  const handleWindowError = (event: ErrorEvent) => {
    const runtimeError = event.error || new Error(event.message || 'Unbekannter Laufzeitfehler');
    addBreadcrumb({
      category: 'window.error',
      message: runtimeError.message,
      data: {
        source: event.filename || null,
        line: event.lineno || null,
        column: event.colno || null,
      },
    });
    void captureError(runtimeError, { level: 'error' }).catch(() => undefined);
  };

  const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
    const reason = event.reason instanceof Error ? event.reason : new Error(String(event.reason || 'Unhandled rejection'));
    addBreadcrumb({
      category: 'unhandledrejection',
      message: reason.message,
      data: null,
    });
    void captureError(reason, { level: 'error' }).catch(() => undefined);
  };

  const init = async () => {
    const cached = readCachedSession();

    if (cached) {
      sessionId = cached.sessionId;
      bindAutoTracking();
      return;
    }

    const response = await fetch(`${baseUrl}/api/projects/${options.projectId}/track/init`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-project-api-key': options.apiKey,
      },
      body: JSON.stringify({
        user,
        clientSessionId: user ? null : getAnonymousVisitorId(),
      }),
    });

    if (!response.ok) {
      throw new Error(`Bug I Am Session konnte nicht initialisiert werden (${response.status}).`);
    }

    const payload = (await response.json()) as { sessionId: string; expiresAt: string };
    sessionId = payload.sessionId;
    writeCachedSession(payload.sessionId, payload.expiresAt);
    bindAutoTracking();
  };

  const addBreadcrumb = (breadcrumb: BugIAmBreadcrumb) => {
    if (!breadcrumb.message) {
      return;
    }

    breadcrumbs = [
      ...breadcrumbs,
      {
        timestamp: new Date().toISOString(),
        category: breadcrumb.category ?? 'custom',
        message: breadcrumb.message,
        data: breadcrumb.data ?? null,
      },
    ].slice(-maxBreadcrumbs);
  };

  const captureError = async (
    error: unknown,
    captureOptions: { level?: BugIAmLevel; errorCode?: string; referrer?: string } = {},
  ) => {
    if (!sessionId) {
      await init();
    }

    if (!sessionId) {
      return;
    }

    const normalized = serializeError(error);
    const response = await fetch(`${baseUrl}/api/projects/${options.projectId}/track/errors`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-bugiam-session-id': sessionId,
      },
      body: JSON.stringify({
        name: normalized.name,
        message: normalized.message,
        stackTrace: normalized.stackTrace,
        level: captureOptions.level ?? defaultLevel,
        errorCode: captureOptions.errorCode,
        release: options.release,
        user,
        referrer: captureOptions.referrer ?? window.location.href,
        breadcrumbs,
      }),
    });

    if (!response.ok) {
      if (response.status === 403) {
        clearCachedSession();
      }
      throw new Error(`Bug I Am Error konnte nicht uebermittelt werden (${response.status}).`);
    }
  };

  return {
    init,
    setUser(nextUser: unknown) {
      const previousUserKey = currentUserKey();
      user = nextUser;
      if (previousUserKey !== currentUserKey()) {
        clearCachedSession();
        void init().catch(() => undefined);
      }
    },
    addBreadcrumb,
    captureError,
  };
}

function toTrackedUser() {
  if (!authState.user) {
    return null;
  }

  return {
    id: authState.user.id,
    email: authState.user.email,
    name: authState.user.name,
    role: authState.user.role,
  };
}

function bindRouter(client: BugIAmClient, router: Router) {
  router.afterEach((to: RouteLocationNormalizedLoaded, from: RouteLocationNormalizedLoaded) => {
    client.addBreadcrumb({
      category: 'navigation',
      message: `Navigation zu ${to.fullPath}`,
      data: {
        from: from.fullPath,
      },
    });
  });

  router.onError((error) => {
    client.addBreadcrumb({
      category: 'router.error',
      message: error.message || 'Vue Router Fehler',
      data: null,
    });
    void client.captureError(error, { level: 'error' });
  });
}

export function createBugIAmVuePlugin(options: BugIAmOptions): Plugin {
  return {
    install(app: App) {
      const client = createClient(options);
      const previousErrorHandler = app.config.errorHandler;
      const handleExternalUserSet = (event: Event) => {
        client.setUser((event as CustomEvent).detail ?? null);
      };
      const handleExternalUserClear = () => {
        client.setUser(null);
      };

      client.setUser(toTrackedUser());
      void client.init().catch(() => undefined);

      app.config.errorHandler = (error, instance, info) => {
        client.addBreadcrumb({
          category: 'vue.errorHandler',
          message: info || 'Vue Fehler',
          data: {
            component: instance?.$options?.name || null,
          },
        });
        void client.captureError(error, { level: 'error' }).catch(() => undefined);
        previousErrorHandler?.(error, instance, info);
      };

      if (options.router) {
        bindRouter(client, options.router);
      }

      window.addEventListener(BUGIAM_USER_SET_EVENT, handleExternalUserSet as EventListener);
      window.addEventListener(BUGIAM_USER_CLEAR_EVENT, handleExternalUserClear);

      app.config.globalProperties.$bugIam = client;
      app.provide('bugiam', client);

      watch(
        () => authState.user,
        () => {
          client.setUser(toTrackedUser());
        },
        { deep: true },
      );
    },
  };
}
